import { debounce } from '@bs/utils';
import { EVENTS } from '@bs/constants';

/**
 * ID
 * @type {number}
 * @ignore
 */
let id = 0;

/**
 * Get ID
 *
 * Because file is loaded only once, this function
 * can be used to generate a unique id every time
 * it is called.
 * @returns {number} Unique ID value
 * @ignore
 */
function getId() {
  id += 1;
  return id;
}

/**
 * Scroll Service
 */
export default class ScrollService {
  /**
   * Scroll Service constructor in which the `callbacks` array is created
   * as a property of the class.
   */
  constructor() {
    /**
     * An array to be populated with callback functions that will be triggered on scroll
     * @property {Array} callbacks - The array of callback functions to be triggered on scroll.
     */
    this.callbacks = [];

    this.init();
  }

  /**
   * @description Initialize the singleton by attaching the event listener to the window
   * @listener {Event} listens to the window scroll event
   */
  init() {
    window.addEventListener(
      EVENTS.SCROLL,
      debounce(this.onScroll.bind(this), 10)
    );
  }

  /**
   * @description The scroll event handler. Iterates through the `callback` array and invokes each callback in the Array
   */
  onScroll() {
    this.callbacks.forEach((callback) => {
      callback.callback();
    });
  }

  /**
   * @description A hook for pushing a callback function into the `callbacks` array. A unique
   * ID value for the callback is generated and a function is returned for removing
   * the callback if need be.
   * @param {Function} callback A function to invoke by the ScrollService
   * @returns {Function} `removeCallback` A function which will remove an entry from the `callbacks` array
   */
  addCallback(callback) {
    // Generate an id for the callback
    const newId = getId();

    // Push function into array with a unique id
    this.callbacks.push({
      id: newId,
      callback
    });

    // Return the remove function
    return this.removeCallback.bind(this, newId);
  }

  /**
   * Filters through the `callback` array and removes
   * the entry that corresponds to the id passed
   * in as an argument
   * @param  {number} newId An id value to filter by
   */
  removeCallback(newId) {
    this.callbacks = this.callbacks.filter((item) => item.id !== newId);
  }
}
